import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { putawayRows } from '../../../../../features/putaway/putawayThunks';
import {
  bulkDoneNotice,
  bulkProblemLines,
  resolveDestination,
  unitsOf,
} from '../../../../../features/putaway/putawayUtils';
import { formatQuantity, zoneTypeLabel } from '../../../../../utils/inventoryLabels';

/**
 * Puts every selected row away in ONE location.
 *
 * The case of "this whole delivery goes to rack B-03": the destination is
 * chosen once — from the list, or **typed or scanned as a code** that has to
 * match whole, Enter confirming — and every selected row goes there with its
 * full quantity. All or nothing: the server names every row it refused, and
 * the dialog lists them by SKU so the sheet of selections is corrected once.
 *
 * @param {object} props
 * @param {Array<object>|null} props.rows - The selected pending rows; `null` keeps it closed.
 * @param {object} props.warehouse - The warehouse in use.
 * @param {Array<object>} props.destinations - Active storage and picking locations.
 * @param {() => void} props.onClose
 * @param {(notice: {type: string, message: string}) => void} props.onDone
 */
export const BulkPutawayModal = ({ rows, warehouse, destinations, onClose, onDone }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector((state) => Boolean(state.putaway.busyKey));

  const [pickedId, setPickedId] = useState(null);
  const [typed, setTyped] = useState('');
  const [failure, setFailure] = useState(null);

  const isOpen = Boolean(rows && rows.length > 0);

  // Every opening starts without a destination.
  useEffect(() => {
    if (!isOpen) return;
    setPickedId(null);
    setTyped('');
    setFailure(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const picked = destinations.find((slot) => slot.id === pickedId) || null;
  const destination = typed.trim() ? resolveDestination(typed, destinations) : picked;

  const problem = (() => {
    if (destination) return null;
    return typed.trim()
      ? `No existe «${typed.trim()}» en ${warehouse?.name || 'esta bodega'}`
      : 'Elige la ubicación destino';
  })();

  const options = destinations.map((slot) => {
    const code = slot.code && slot.code !== slot.name ? ` · ${slot.code}` : '';
    return {
      value: slot.id,
      label: `${slot.name}${code} — ${zoneTypeLabel(slot.zone_type)}`,
      shortLabel: slot.name,
    };
  });

  const handleSave = async () => {
    if (problem || isSaving) return;
    setFailure(null);
    const action = await dispatch(putawayRows({ rows, slotId: destination.id }));
    if (action.meta.requestStatus !== 'fulfilled') {
      setFailure(action.payload);
      return;
    }
    onDone(bulkDoneNotice(action.payload, destination.name));
    onClose();
  };

  const units = unitsOf(rows);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Guardar todas en una ubicación"
      testId="putaway-bulk-modal"
      busy={isSaving}
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6" data-testid="putaway-bulk-summary">
        {rows.length} {rows.length === 1 ? 'fila' : 'filas'} · {formatQuantity(units)} un
      </p>

      <div className="space-y-4">
        {failure && (
          <div
            data-testid="putaway-bulk-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm space-y-2"
          >
            <p>{failure.message}</p>
            {bulkProblemLines(failure, rows).length > 0 && (
              <ul className="list-disc pl-5 space-y-0.5 text-xs">
                {bulkProblemLines(failure, rows).map((line, index) => (
                  <li key={index} data-testid="putaway-bulk-problem">
                    {line}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <FilterableSelect
          variant="form"
          testId="putaway-bulk-destination"
          label="Ubicación destino"
          // What is typed wins over what was picked, so the list shows it too.
          value={destination?.id ?? null}
          onChange={(slotId) => {
            setPickedId(slotId);
            setTyped('');
          }}
          options={options}
          placeholder="Seleccionar..."
          searchPlaceholder="Buscar ubicación..."
          disabled={isSaving}
        />

        <Field label="o escribe / escanea su código" htmlFor="putaway-bulk-code">
          <input
            id="putaway-bulk-code"
            type="text"
            data-testid="putaway-bulk-code"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            // A scanner ends its read with Enter: that is the confirmation.
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSave();
            }}
            placeholder="Código de la ubicación"
            autoComplete="off"
            disabled={isSaving}
            className={inputClass}
          />
        </Field>

        <p className="text-xs text-text-muted">
          Solo Almacenaje y Picking dejan el stock disponible para vender. Es todo o nada: si una
          fila no se puede guardar, no se guarda ninguna y se te dice cuáles.
        </p>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing. */}
        <Button
          variant="primary"
          loading={isSaving}
          loadingLabel="Guardando..."
          disabled={Boolean(problem)}
          onClick={handleSave}
          data-testid="putaway-bulk-save"
        >
          {problem || `Guardar ${rows.length === 1 ? 'la fila' : `las ${rows.length} filas`} aquí`}
        </Button>
      </div>
    </Modal>
  );
};
